/**
 * FormBuilder — full-page admin editor for one form (/crm/settings/forms/:id),
 * rebuilt to mirror the WCR website's questionnaire admin:
 *
 *   - Two tabs: Questions and Settings.
 *   - Questions tab is a drag-and-drop list of "slots" where the locked
 *     Contact Capture card is interleaved among the questions at
 *     settings.contact_capture_position (drag it, or use the up/down buttons,
 *     to move it — the feature parity the old SiteOps builder lacked). On
 *     mobile, HTML5 drag is unavailable, so the cards expose up/down controls.
 *   - Questions are edited in a modal (type picker → details) with the full
 *     WCR customization surface: branching, eligibility (exclude/flag),
 *     calculations.
 *   - Settings tab exposes every questionnaire-level field the data model
 *     already round-trips for the public WCR site: outcome messages, greeting +
 *     bullets, post-contact message, confirmation CTA, redirect, contact method.
 *
 * The form's working copy lives in crm.forms.definition (draft); Publish
 * snapshots it into an immutable form_versions row that texted links pin to.
 * Question keys are minted ONCE and never change. settings.* keys are accepted
 * verbatim by crmForms.validateDefinition and hoisted back out for the WCR
 * renderer by formToWcrQuestionnaire — so NO backend change is needed here.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Copy, Eye, Loader2, Plus, UploadCloud, X } from 'lucide-react';
import api from '../../../services/api';
import { CrmForm, FormDefinition, FormQuestionDef, FormVersionSummary } from '../../types';
import { useCanEditForms, FORM_SURFACES, formSurface } from '../../lib';
import FormRenderer, { FormAnswers } from '../../components/forms/FormRenderer';
import QuestionCard from '../../components/forms/QuestionCard';
import ContactCaptureCard from '../../components/forms/ContactCaptureCard';
import QuestionEditorModal from '../../components/forms/QuestionEditorModal';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

type ContactMethod = 'phone' | 'email';
const DEFAULT_CONTACT_POS = 3;

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
];

interface StudyChip { id: string | null; slug: string; title: string | null; show_on_website?: boolean; }

// A row in the builder list: a question or the contact-capture card.
type Slot = { kind: 'contact' } | { kind: 'question'; q: FormQuestionDef; index: number };

/** Interleave the contact-capture card among the questions at `pos` (index
 * into the full questions array). Mirrors the WCR builder's slot model. */
function buildSlots(questions: FormQuestionDef[], pos: number): Slot[] {
  const out: Slot[] = [];
  let inserted = false;
  questions.forEach((q, i) => {
    if (!inserted && i >= pos) { out.push({ kind: 'contact' }); inserted = true; }
    out.push({ kind: 'question', q, index: i });
  });
  if (!inserted) out.push({ kind: 'contact' });
  return out;
}

/** Move the slot at fromIdx to toIdx (one drag-over step / one button press),
 * returning the new { questions, pos } or null for a no-op. Faithful port of
 * the WCR builder's handleDragOver reconciliation. */
function stepReorder(
  questions: FormQuestionDef[],
  pos: number,
  fromIdx: number,
  toIdx: number,
): { questions: FormQuestionDef[]; pos: number } | null {
  const slots = buildSlots(questions, pos);
  if (fromIdx < 0 || toIdx < 0 || fromIdx >= slots.length || toIdx >= slots.length || fromIdx === toIdx) return null;
  const dragged = slots[fromIdx];
  const target = slots[toIdx];

  if (dragged.kind === 'contact') {
    if (target.kind !== 'question') return null;
    const newPos = fromIdx > toIdx ? target.index : target.index + 1;
    return newPos === pos ? null : { questions, pos: newPos };
  }

  // Dragged is a question.
  if (target.kind === 'contact') {
    // Adjacent swap with the contact card — every move-button press and every
    // continuous-drag step that crosses the card. The question and the card
    // simply trade places: only `pos` moves, the questions array is untouched.
    // The card's slot index IS `pos`, so it shifts opposite to the question —
    // question moving DOWN (fromIdx<toIdx) → card rises → pos-1; UP → pos+1.
    if (Math.abs(fromIdx - toIdx) === 1) {
      const newPos = fromIdx < toIdx ? Math.max(0, pos - 1) : Math.min(questions.length, pos + 1);
      return newPos === pos ? null : { questions, pos: newPos };
    }
    // Multi-slot drag jump landing on the card: reorder the array so the
    // question lands on the far side, at the card boundary (WCR drag math).
    const dragIdx = dragged.index;
    const nq = questions.slice();
    const [dq] = nq.splice(dragIdx, 1);
    let insertAt: number;
    if (fromIdx < toIdx) { // moving down, past the contact card
      insertAt = pos;
      if (dragIdx < pos) insertAt = Math.max(0, insertAt - 1);
    } else { // moving up, past the contact card
      insertAt = Math.max(0, pos - 1);
    }
    insertAt = Math.min(insertAt, nq.length);
    nq.splice(insertAt, 0, dq);
    return { questions: nq, pos };
  }

  // Reorder two questions among themselves; shift pos if the move crossed it.
  const fromQ = dragged.index;
  const toQ = target.index;
  const nq = questions.slice();
  const [dq] = nq.splice(fromQ, 1);
  nq.splice(toQ, 0, dq);
  let newPos = pos;
  if (fromQ < pos && toQ >= pos) newPos = Math.max(0, pos - 1);
  else if (fromQ >= pos && toQ < pos) newPos = pos + 1;
  return { questions: nq, pos: newPos };
}

/** Drop references to a removed question so the draft stays valid. */
function stripRefs(questions: FormQuestionDef[], removedKey: string | undefined): FormQuestionDef[] {
  if (!removedKey) return questions;
  return questions.map((q) => {
    let next = q;
    if (next.show_if?.question_key === removedKey) next = { ...next, show_if: null };
    if (next.calculation && Object.values(next.calculation.inputs).includes(removedKey)) {
      const inputs = Object.fromEntries(Object.entries(next.calculation.inputs).filter(([, ref]) => ref !== removedKey));
      next = { ...next, calculation: { ...next.calculation, inputs } };
    }
    return next;
  });
}

const fieldInput =
  'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500';
const fieldLabel = 'mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300';

export default function FormBuilder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canEdit = useCanEditForms();

  const [form, setForm] = useState<CrmForm | null>(null);
  const [versions, setVersions] = useState<FormVersionSummary[]>([]);
  const [definition, setDefinition] = useState<FormDefinition>({ questions: [], settings: {} });
  const [meta, setMeta] = useState({ title: '', surface: 'questionnaire', status: 'draft', study_id: '' as string, description: '' });
  const [studies, setStudies] = useState<StudyChip[]>([]);
  const [dirty, setDirty] = useState(false);
  const [tab, setTab] = useState<'questions' | 'settings'>('questions');

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showPreview, setShowPreview] = useState(false);
  const [previewAnswers, setPreviewAnswers] = useState<FormAnswers>({});
  const [draggedSlot, setDraggedSlot] = useState<number | null>(null);

  // Question editor modal.
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [formRes, studiesRes] = await Promise.all([
        api.get(`/api/crm/forms/${id}`),
        api.get('/api/crm/studies'),
      ]);
      const f: CrmForm = formRes.data.form;
      setForm(f);
      setVersions(formRes.data.versions || []);
      const rawSettings = (f.definition && f.definition.settings) || {};
      // Normalize the two placement settings so the builder is deterministic and
      // matches what the WCR site will serve. Done WITHOUT marking dirty — it is
      // initial state, not a user edit; the first real save persists it.
      const settings: Record<string, any> = { ...rawSettings };
      if (!Number.isFinite(Number(settings.contact_capture_position))) settings.contact_capture_position = DEFAULT_CONTACT_POS;
      else settings.contact_capture_position = Number(settings.contact_capture_position);
      if (settings.contact_method !== 'email' && settings.contact_method !== 'phone') settings.contact_method = 'phone';
      setDefinition({
        questions: f.definition && Array.isArray(f.definition.questions) ? f.definition.questions : [],
        settings,
      });
      setMeta({ title: f.title, surface: formSurface(f.surface), status: f.status, study_id: f.study_id || '', description: f.description || '' });
      setStudies((studiesRes.data?.studies || []).filter((s: StudyChip) => s.id));
      setDirty(false);
    } catch (e: any) {
      setLoadError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  // Duplicate navigates /forms/:a -> /forms/:b within this same component, so the
  // busy flag is cleared when the id actually changes (not on every reload).
  useEffect(() => { setDuplicating(false); }, [id]);

  const touch = () => { setDirty(true); setNotice(null); };

  const setSetting = (key: string, value: any) => {
    setDefinition((d) => ({ ...d, settings: { ...d.settings, [key]: value } }));
    touch();
  };

  const pos = useMemo(() => {
    const n = Number(definition.settings.contact_capture_position);
    return Number.isFinite(n) ? n : DEFAULT_CONTACT_POS;
  }, [definition.settings.contact_capture_position]);

  // Landing forms collect contact info via the interleaved Contact Capture card.
  // Study questionnaires are texted to a lead we ALREADY have (and are talking
  // to), so they're a plain question list — no contact-capture step.
  const isLanding = meta.surface === 'landing';
  const contactMethod: ContactMethod = definition.settings.contact_method === 'email' ? 'email' : 'phone';
  const slots = useMemo(
    () => (isLanding
      ? buildSlots(definition.questions, pos)
      : definition.questions.map((q, index) => ({ kind: 'question' as const, q, index }))),
    [isLanding, definition.questions, pos],
  );

  const postContactWarning = useMemo(() => {
    if (!isLanding || pos <= 0) return null;
    const ci = slots.findIndex((s) => s.kind === 'contact');
    if (ci === -1 || ci >= slots.length - 1) return null;
    const next = slots[ci + 1];
    if (next.kind !== 'question') return null;
    if (next.q.type !== 'single_choice' && next.q.type !== 'yes_no') {
      return 'For best conversion, the question right after Contact Capture should be a single-choice or yes/no question.';
    }
    return null;
  }, [slots, pos, isLanding]);

  // --- Slot reordering (drag + buttons) ---
  const applyReorder = useCallback((fromIdx: number, toIdx: number) => {
    let changed = false;
    setDefinition((d) => {
      // Questionnaire: slot index === question index (no contact card to weave around).
      if (!isLanding) {
        if (fromIdx < 0 || toIdx < 0 || fromIdx >= d.questions.length || toIdx >= d.questions.length || fromIdx === toIdx) return d;
        const nq = d.questions.slice();
        const [moved] = nq.splice(fromIdx, 1);
        nq.splice(toIdx, 0, moved);
        changed = true;
        return { ...d, questions: nq };
      }
      const p = Number.isFinite(Number(d.settings.contact_capture_position)) ? Number(d.settings.contact_capture_position) : DEFAULT_CONTACT_POS;
      const res = stepReorder(d.questions, p, fromIdx, toIdx);
      if (!res) return d;
      changed = true;
      return { questions: res.questions, settings: { ...d.settings, contact_capture_position: res.pos } };
    });
    if (changed) touch();
  }, [isLanding]);

  const onDragOver = (e: React.DragEvent, targetIdx: number) => {
    e.preventDefault();
    if (draggedSlot === null || draggedSlot === targetIdx) return;
    applyReorder(draggedSlot, targetIdx);
    setDraggedSlot(targetIdx);
  };

  // --- Question CRUD ---
  const openAdd = () => { setEditingIndex(null); setEditorOpen(true); };
  const openEdit = (origIndex: number) => { setEditingIndex(origIndex); setEditorOpen(true); };

  const saveQuestion = (q: FormQuestionDef) => {
    setDefinition((d) => {
      const list = d.questions.slice();
      if (editingIndex != null && editingIndex < list.length) list[editingIndex] = q;
      else list.push(q);
      return { ...d, questions: list };
    });
    touch();
    setEditorOpen(false);
    setEditingIndex(null);
  };

  const removeQuestion = (origIndex: number) => {
    setDefinition((d) => {
      const removedKey = d.questions[origIndex]?.key;
      const rest = stripRefs(d.questions.filter((_, j) => j !== origIndex), removedKey);
      let p = Number.isFinite(Number(d.settings.contact_capture_position)) ? Number(d.settings.contact_capture_position) : DEFAULT_CONTACT_POS;
      if (origIndex < p) p = Math.max(0, p - 1);
      return { questions: rest, settings: { ...d.settings, contact_capture_position: p } };
    });
    touch();
  };

  // --- Save / publish ---
  const answerableCount = useMemo(
    () => definition.questions.filter((q) => q.type !== 'calculation').length,
    [definition.questions],
  );

  const saveDraft = useCallback(async (): Promise<boolean> => {
    if (saving) return false;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.patch(`/api/crm/forms/${id}`, {
        title: meta.title,
        surface: meta.surface,
        status: meta.status,
        study_id: meta.study_id || null,
        description: meta.description || null,
        definition,
      });
      setForm(res.data.form);
      setDirty(false);
      setNotice('Draft saved.');
      return true;
    } catch (e: any) {
      setError(errMsg(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [saving, id, meta, definition]);

  // Copy this form's questions + settings into a fresh draft (a starting template),
  // then jump to the new form. The copy keeps this form's study link.
  const duplicate = async () => {
    if (duplicating || saving) return;
    // The copy is made from the saved draft, so flush unsaved edits first
    // (same as publish) — otherwise the duplicate would miss them.
    if (dirty) {
      const ok = await saveDraft();
      if (!ok) return;
    }
    setDuplicating(true);
    setError(null);
    try {
      const res = await api.post(`/api/crm/forms/${id}/duplicate`, {});
      navigate(`/crm/settings/forms/${res.data.form.id}`);
    } catch (e: any) {
      setError(errMsg(e));
      setDuplicating(false);
    }
  };

  const publish = async () => {
    if (publishing) return;
    if (dirty) {
      const ok = await saveDraft();
      if (!ok) return;
    }
    setPublishing(true);
    setError(null);
    try {
      const res = await api.post(`/api/crm/forms/${id}/publish`);
      setNotice(`Published version ${res.data?.version?.version}. Texted links now use it.`);
      await load();
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setPublishing(false);
    }
  };

  // --- Preview (live show_if; same looseEq tolerance as PublicForm/crmForms) ---
  const previewQuestions = useMemo(
    () => definition.questions
      .filter((q) => q.type !== 'calculation')
      .map((q) => ({
        key: q.key,
        label: q.label || '(untitled question)',
        type: q.type,
        options: q.options,
        required: q.required !== false,
        helpText: q.description,
        placeholder: q.placeholder,
        min: q.min,
        max: q.max,
      })),
    [definition.questions],
  );

  const visiblePreview = useMemo(() => previewQuestions.filter((pq) => {
    const def = definition.questions.find((q) => q.key === pq.key);
    if (!def?.show_if) return true;
    const ans = previewAnswers[def.show_if.question_key];
    if (ans === undefined || ans === null) return false;
    const eq = ans === def.show_if.value || String(ans) === String(def.show_if.value);
    switch (def.show_if.operator) {
      case 'equals': return eq;
      case 'not_equals': return !eq;
      case 'contains': return String(ans).includes(String(def.show_if.value));
      case 'greater_than': return Number(ans) > Number(def.show_if.value);
      case 'less_than': return Number(ans) < Number(def.show_if.value);
      default: return false;
    }
  }), [previewQuestions, definition.questions, previewAnswers]);

  const editingQuestion = editingIndex != null ? definition.questions[editingIndex] || null : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-7 w-7 animate-spin text-gray-400" />
      </div>
    );
  }
  if (loadError || !form) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center">
        <p className="text-sm text-red-600 dark:text-red-400">{loadError || 'Form not found'}</p>
        <Link to="/crm/settings/forms" className="mt-3 inline-block text-sm font-medium text-primary-700 hover:underline dark:text-primary-400">
          Back to forms
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
      <Link to="/crm/settings/forms" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
        <ArrowLeft className="h-4 w-4" /> Forms
      </Link>

      {/* Header */}
      <div className="mt-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <input
              type="text"
              value={meta.title}
              disabled={!canEdit}
              onChange={(e) => { setMeta((m) => ({ ...m, title: e.target.value })); touch(); }}
              placeholder="Form title"
              className="w-full max-w-xl rounded-lg border border-transparent bg-transparent px-2 py-1 text-xl font-semibold text-gray-900 hover:border-gray-200 focus:border-primary-500 focus:outline-none dark:text-gray-100 dark:hover:border-gray-600"
            />
            <p className="mt-1 px-2 text-xs text-gray-400">
              {form.version > 0 ? `Published v${form.version}` : 'Never published'}
              {form.imported_questionnaire_id ? ' · imported from WCR' : ''}
              {dirty ? ' · unsaved changes' : ''}
            </p>
          </div>
          {canEdit && (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <button
                onClick={() => setShowPreview((v) => !v)}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg sm:min-h-0 border border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <Eye className="h-4 w-4" /> {showPreview ? 'Hide preview' : 'Preview'}
              </button>
              <button
                onClick={duplicate}
                disabled={duplicating}
                title="Copy this form's questions and settings into a new draft"
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg sm:min-h-0 border border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {duplicating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
                {duplicating ? 'Duplicating…' : 'Duplicate'}
              </button>
              <button
                onClick={saveDraft}
                disabled={saving || !dirty}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg sm:min-h-0 border border-primary-600 px-3 py-2 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {saving ? 'Saving…' : 'Save draft'}
              </button>
              <button
                onClick={publish}
                disabled={publishing || saving || answerableCount === 0}
                title={answerableCount === 0 ? 'Add at least one question first' : 'Snapshot this draft as the live version'}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg sm:min-h-0 bg-primary-600 px-3 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                {publishing ? 'Publishing…' : 'Publish'}
              </button>
            </div>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Group</span>
            <select
              value={meta.surface}
              disabled={!canEdit}
              onChange={(e) => { setMeta((m) => ({ ...m, surface: e.target.value })); touch(); }}
              className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            >
              {FORM_SURFACES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <p className="mt-1 text-[11px] text-gray-400">
              {meta.surface === 'landing'
                ? 'Served on the public WCR ad page (/study/<slug>).'
                : 'Texted to leads on their phone (the /f link).'}
            </p>
          </div>
          <div>
            <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Status</span>
            <select
              value={meta.status}
              disabled={!canEdit}
              onChange={(e) => { setMeta((m) => ({ ...m, status: e.target.value })); touch(); }}
              className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            >
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        {notice && (
          <div className="mt-3 rounded-lg border border-primary-200 bg-primary-50 p-2.5 text-xs text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
            {notice}
          </div>
        )}
        {error && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {error}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-5 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-4">
          {(['questions', 'settings'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`border-b-2 px-1 py-3 text-sm font-medium transition-colors duration-150 ${
                tab === t
                  ? 'border-primary-500 text-primary-700 dark:border-primary-400 dark:text-primary-300'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {t === 'questions' ? `Questions (${definition.questions.length})` : 'Settings'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'questions' ? (
        <div className={`mt-5 grid grid-cols-1 gap-5 ${showPreview ? 'lg:grid-cols-2' : ''}`}>
          <div className="space-y-3">
            {!isLanding && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-400">
                Texted to a lead you already have and are talking to — no contact-capture step.
                Add the detailed questions you need (address, eligibility follow-ups, etc.).
              </div>
            )}
            {/* Slot list */}
            {slots.map((slot, slotIdx) => {
              const key = slot.kind === 'contact' ? 'contact-capture' : slot.q.key;
              return (
                <div
                  key={key}
                  draggable={canEdit}
                  onDragStart={() => setDraggedSlot(slotIdx)}
                  onDragOver={(e) => onDragOver(e, slotIdx)}
                  onDragEnd={() => setDraggedSlot(null)}
                  className={draggedSlot === slotIdx ? 'opacity-50 transition-opacity' : 'transition-opacity'}
                >
                  {slot.kind === 'contact' ? (
                    <ContactCaptureCard
                      contactMethod={contactMethod}
                      onContactMethodChange={(m) => setSetting('contact_method', m)}
                      canEdit={canEdit}
                      onMoveUp={() => applyReorder(slotIdx, slotIdx - 1)}
                      onMoveDown={() => applyReorder(slotIdx, slotIdx + 1)}
                      canMoveUp={slotIdx > 0}
                      canMoveDown={slotIdx < slots.length - 1}
                    />
                  ) : (
                    <QuestionCard
                      question={slot.q}
                      number={slot.index + 1}
                      canEdit={canEdit}
                      onEdit={() => openEdit(slot.index)}
                      onDelete={() => removeQuestion(slot.index)}
                      onMoveUp={() => applyReorder(slotIdx, slotIdx - 1)}
                      onMoveDown={() => applyReorder(slotIdx, slotIdx + 1)}
                      canMoveUp={slotIdx > 0}
                      canMoveDown={slotIdx < slots.length - 1}
                    />
                  )}
                </div>
              );
            })}

            {definition.questions.length === 0 && (
              <p className="px-1 text-sm text-gray-500 dark:text-gray-400">
                No questions yet — add one, or import a WCR questionnaire from the Forms list.
              </p>
            )}

            {postContactWarning && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <p className="text-sm text-amber-700 dark:text-amber-300">{postContactWarning}</p>
              </div>
            )}

            {canEdit && (
              <button
                onClick={openAdd}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg sm:min-h-0 border border-primary-600 px-3 py-2 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
              >
                <Plus className="h-4 w-4" /> Add Question
              </button>
            )}
          </div>

          {/* Live preview */}
          {showPreview && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100">Preview</h3>
              <p className="mb-4 text-xs text-gray-400">
                {isLanding
                  ? 'The landing page shows ONE question per screen — this preview lists them all. Branching applies live.'
                  : 'The texted questionnaire shows every question on one scrollable page, just like this preview. Branching applies live.'}
              </p>
              <FormRenderer
                questions={visiblePreview}
                answers={previewAnswers}
                onAnswer={(k, v) => setPreviewAnswers((a) => ({ ...a, [k]: v }))}
              />
            </div>
          )}
        </div>
      ) : (
        <SettingsTab
          definition={definition}
          meta={meta}
          studies={studies}
          canEdit={canEdit}
          isLanding={isLanding}
          pos={pos}
          versions={versions}
          liveVersion={form.version}
          onMeta={(patch) => { setMeta((m) => ({ ...m, ...patch })); touch(); }}
          onSetting={setSetting}
          fieldInput={fieldInput}
          fieldLabel={fieldLabel}
        />
      )}

      <QuestionEditorModal
        isOpen={editorOpen}
        question={editingQuestion}
        allQuestions={definition.questions}
        onSave={saveQuestion}
        onClose={() => { setEditorOpen(false); setEditingIndex(null); }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings tab
// ---------------------------------------------------------------------------

interface SettingsTabProps {
  definition: FormDefinition;
  meta: { title: string; surface: string; status: string; study_id: string; description: string };
  studies: StudyChip[];
  canEdit: boolean;
  isLanding: boolean;
  pos: number;
  versions: FormVersionSummary[];
  liveVersion: number;
  onMeta: (patch: Partial<{ study_id: string; description: string }>) => void;
  onSetting: (key: string, value: any) => void;
  fieldInput: string;
  fieldLabel: string;
}

function SettingsTab({
  definition, meta, studies, canEdit, isLanding, pos, versions, liveVersion, onMeta, onSetting, fieldInput, fieldLabel,
}: SettingsTabProps) {
  const s = definition.settings;
  const bullets: string[] = Array.isArray(s.greeting_bullets) ? s.greeting_bullets : [];
  const ro = !canEdit;
  // The study this landing form is linked to — used to preview the redirect
  // target (/studies/<slug>) and to warn when that page isn't published yet.
  const linkedStudy = studies.find((st) => st.id === meta.study_id) || null;
  const redirectToStudyInfo = Boolean(s.redirect_to_study_info);

  const setBullet = (i: number, v: string) => onSetting('greeting_bullets', bullets.map((b, j) => (j === i ? v : b)));
  // Cap at 20 — the backend settings whitelist rejects a longer array, which
  // would 400 the whole draft save with a message that never names bullets.
  const addBullet = () => { if (bullets.length < 20) onSetting('greeting_bullets', [...bullets, '']); };
  const removeBullet = (i: number) => {
    const next = bullets.filter((_, j) => j !== i);
    onSetting('greeting_bullets', next.length ? next : null);
  };

  return (
    <div className="mt-5 space-y-5">
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <div className="space-y-5">
          {/* Greeting */}
          <div>
            <label className="flex items-center gap-2.5 text-sm font-medium text-gray-700 dark:text-gray-200">
              <input type="checkbox" disabled={ro} checked={Boolean(s.greeting_enabled)} onChange={(e) => onSetting('greeting_enabled', e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700" />
              Show a greeting page before the questionnaire
            </label>
            {Boolean(s.greeting_enabled) && (
              <div className="mt-3 space-y-3">
                <div>
                  <span className={fieldLabel}>Greeting message</span>
                  <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">A welcome splash shown before the first question.</p>
                  <textarea rows={3} maxLength={2000} disabled={ro} value={s.greeting_message || ''} onChange={(e) => onSetting('greeting_message', e.target.value || null)} placeholder="Welcome! This short questionnaire will help us see if you may be a good fit for this study." className={`${fieldInput} border-gray-300 focus:border-primary-500 focus:ring-primary-500/20 dark:border-gray-600`} />
                </div>
                <div>
                  <span className={fieldLabel}>Bullet points (optional)</span>
                  <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">Quick highlights shown on the greeting page.</p>
                  <div className="space-y-2">
                    {bullets.map((b, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input disabled={ro} maxLength={500} value={b} onChange={(e) => setBullet(i, e.target.value)} placeholder={`Bullet ${i + 1}`} className={`${fieldInput} flex-1 border-gray-300 focus:border-primary-500 focus:ring-primary-500/20 dark:border-gray-600`} />
                        <button type="button" disabled={ro} onClick={() => removeBullet(i)} className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20" aria-label="Remove bullet">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    {canEdit && bullets.length < 20 && (
                      <button type="button" onClick={addBullet} className="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:underline dark:text-primary-400">
                        <Plus className="h-3.5 w-3.5" /> Add bullet
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <span className={fieldLabel}>Description</span>
            <input disabled={ro} value={meta.description} onChange={(e) => onMeta({ description: e.target.value })} placeholder="Brief description of this form (internal)" className={`${fieldInput} border-gray-300 focus:border-primary-500 focus:ring-primary-500/20 dark:border-gray-600`} />
          </div>

          <div>
            <span className={fieldLabel}>Linked study</span>
            <select disabled={ro} value={meta.study_id} onChange={(e) => onMeta({ study_id: e.target.value })} className={`${fieldInput} cursor-pointer border-gray-300 focus:border-primary-500 focus:ring-primary-500/20 dark:border-gray-600`}>
              <option value="">No study linked</option>
              {studies.map((st) => <option key={st.id!} value={st.id!}>{st.title || st.slug}</option>)}
            </select>
          </div>

          {isLanding && pos > 0 && (
            <div>
              <span className={fieldLabel}>Post-contact message</span>
              <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">Shown briefly after the patient submits contact info, before the remaining questions (public website flow).</p>
              <textarea rows={2} maxLength={2000} disabled={ro} value={s.post_contact_message || ''} onChange={(e) => onSetting('post_contact_message', e.target.value || null)} placeholder="Almost done — these last questions help us prepare for your visit." className={`${fieldInput} border-gray-300 focus:border-primary-500 focus:ring-primary-500/20 dark:border-gray-600`} />
            </div>
          )}

          {/* Thank-you message (texted forms; outcome is never revealed) */}
          <div>
            <span className={fieldLabel}>Thank-you message</span>
            <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">Shown after submitting a texted form. The eligibility outcome is never revealed to the patient.</p>
            <textarea rows={2} maxLength={2000} disabled={ro} value={s.completion_message || ''} onChange={(e) => onSetting('completion_message', e.target.value || null)} placeholder="We've received your answers. Our team will review them and reach out about next steps." className={`${fieldInput} border-gray-300 focus:border-primary-500 focus:ring-primary-500/20 dark:border-gray-600`} />
          </div>

          {/* Public-website-flow settings (LANDING forms only): the texted
              questionnaire never reveals an outcome and has no confirmation
              screen/redirect, so these are hidden for study questionnaires. */}
          {isLanding && (
            <>
              {/* Outcome messages (public website flow) */}
              <ColoredMessage label="Eligible message" hint="Shown when a patient passes all prescreening criteria (public website flow)." tone="green" disabled={ro} value={s.eligible_message || ''} onChange={(v) => onSetting('eligible_message', v || null)} placeholder="Great news! Based on your responses, you may be a good fit for this study. Our team will contact you within 1–2 business days." />
              <ColoredMessage label="Needs-review message" hint="Shown when a patient is flagged for the team to review." tone="yellow" disabled={ro} value={s.needs_review_message || ''} onChange={(v) => onSetting('needs_review_message', v || null)} placeholder="Thank you for your responses. Our team will review your information and reach out within 1–2 business days." />
              <ColoredMessage label="Not-eligible message" hint="Shown when a patient is excluded based on their answers." tone="amber" disabled={ro} value={s.not_eligible_message || ''} onChange={(v) => onSetting('not_eligible_message', v || null)} placeholder="Thank you for your interest. Based on your responses, this study may not be the right fit at this time." />

              {/* Confirmation CTA */}
              <div className="border-t border-gray-200 pt-5 dark:border-gray-700">
                <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Confirmation CTA</h3>
                <div className="space-y-3">
                  <div>
                    <span className={fieldLabel}>CTA label</span>
                    <input disabled={ro} value={s.confirmation_cta_label || ''} onChange={(e) => onSetting('confirmation_cta_label', e.target.value || null)} placeholder="Call us now" className={`${fieldInput} border-gray-300 focus:border-primary-500 focus:ring-primary-500/20 dark:border-gray-600`} />
                  </div>
                  <div>
                    <span className={fieldLabel}>CTA phone number (optional)</span>
                    <input type="tel" disabled={ro} value={s.confirmation_cta_phone || ''} onChange={(e) => onSetting('confirmation_cta_phone', e.target.value || null)} placeholder="(555) 555-5555" className={`${fieldInput} border-gray-300 focus:border-primary-500 focus:ring-primary-500/20 dark:border-gray-600`} />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">If provided, shows a call button on the confirmation screen.</p>
                  </div>
                </div>
              </div>

              {/* Drop submitters onto the study's public information page. The
                  landing page is served at /study/<slug>; the info page lives at
                  /studies/<slug> (same slug) and only exists when the study is
                  published (Website tab → "Show on website"). */}
              <div className="border-t border-gray-200 pt-5 dark:border-gray-700">
                <label className="flex items-start gap-2.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                  <input type="checkbox" disabled={ro} checked={redirectToStudyInfo} onChange={(e) => onSetting('redirect_to_study_info', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700" />
                  <span>Send patients to the study information page after they submit</span>
                </label>
                <p className="mt-1 ml-6 text-xs text-gray-500 dark:text-gray-400">
                  After the result screen, everyone who submits is redirected to this study's public information page
                  {linkedStudy?.slug ? <> (<code className="rounded bg-gray-100 px-1 py-0.5 dark:bg-gray-700">/studies/{linkedStudy.slug}</code>)</> : ''}.
                </p>
                {redirectToStudyInfo && linkedStudy && linkedStudy.show_on_website === false && (
                  <p className="mt-1.5 ml-6 text-xs font-medium text-amber-600 dark:text-amber-400">
                    ⚠ “{linkedStudy.title || linkedStudy.slug}” isn't published to the website yet, so that page won't exist (patients will just see their result). Turn on the study's <span className="font-semibold">Website → “Show on website”</span> toggle.
                  </p>
                )}
                {redirectToStudyInfo && s.confirmation_cta_phone && (
                  <p className="mt-1.5 ml-6 text-xs font-medium text-amber-600 dark:text-amber-400">
                    ⚠ The result screen shows for only a few seconds before redirecting, so eligible patients have a brief window to tap your “{s.confirmation_cta_label || 'Text us now'}” button. Clear the CTA above if you'd rather they go straight to the study page.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Version history */}
      {versions.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
          <h3 className="mb-2 text-base font-semibold text-gray-900 dark:text-gray-100">Version history</h3>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {versions.map((v) => (
              <div key={v.id} className="flex items-center justify-between py-2 text-sm">
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  v{v.version}
                  {v.version === liveVersion && (
                    <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">live</span>
                  )}
                </span>
                <span className="text-xs text-gray-400">{new Date(v.published_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ColoredMessage({
  label, hint, tone, value, onChange, placeholder, disabled,
}: {
  label: string;
  hint: string;
  tone: 'green' | 'yellow' | 'amber';
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  disabled: boolean;
}) {
  const tones = {
    green: { label: 'text-green-700 dark:text-green-400', input: 'border-green-300 focus:border-green-500 focus:ring-green-500/20 dark:border-green-800' },
    yellow: { label: 'text-yellow-700 dark:text-yellow-400', input: 'border-yellow-300 focus:border-yellow-500 focus:ring-yellow-500/20 dark:border-yellow-800' },
    amber: { label: 'text-amber-700 dark:text-amber-400', input: 'border-amber-300 focus:border-amber-500 focus:ring-amber-500/20 dark:border-amber-800' },
  }[tone];
  return (
    <div>
      <span className={`mb-1.5 block text-sm font-medium ${tones.label}`}>{label}</span>
      <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      <textarea
        rows={3}
        maxLength={2000}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`block w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 ${tones.input}`}
      />
    </div>
  );
}
